import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { Building2, Users, MapPin, Star } from "lucide-react";
import { getLandingStats } from "../../api/services/publicFormService";

const DEFAULTS = {
  happy_guests: 600,
  properties: 10,
  cities: 2,
  average_rating: 4.5,
};

const statConfig = [
  { icon: Users,     key: "happy_guests",   suffix: "+", label: "Bookings",   decimals: 0 },
  { icon: Building2, key: "properties",      suffix: "+", label: "Properties",     decimals: 0 },
  { icon: MapPin,    key: "cities",          suffix: "+", label: "Cities",         decimals: 0 },
  { icon: Star,      key: "average_rating",  suffix: "",  label: "Average Rating", decimals: 1 },
] as const;

type StatsData = typeof DEFAULTS;

const AnimatedNumber = ({
  value,
  decimals = 0,
}: {
  value: number;
  decimals?: number;
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { duration: 2000 });
  const isInView = useInView(ref, { once: true });
  const [displayValue, setDisplayValue] = useState("0");

  useEffect(() => {
    if (isInView) {
      motionValue.set(value);
    }
  }, [isInView, value, motionValue]);

  useEffect(() => {
    const unsubscribe = springValue.on("change", (latest) => {
      setDisplayValue(latest.toFixed(decimals));
    });
    return unsubscribe;
  }, [springValue, decimals]);

  return <span ref={ref}>{displayValue}</span>;
};

const StatsSection = () => {
  const [stats, setStats] = useState<StatsData>(DEFAULTS);

  useEffect(() => {
    getLandingStats()
      .then((data) => setStats(data))
      .catch(() => {});
  }, []);

  return (
    <section className="py-16 bg-primary relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-32 h-32 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-secondary rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {statConfig.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 mb-4">
                <stat.icon className="w-7 h-7 text-secondary" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-primary-foreground mb-2">
                <AnimatedNumber value={stats[stat.key]} decimals={stat.decimals} />
                {stat.suffix}
              </div>
              <p className="text-primary-foreground/70 font-medium">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
